import React, {useDeferredValue, useMemo, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {Link} from 'react-router-dom';
import {proyectosApi} from '../../../api/api';
import {ApiError} from '../../../api/http';
import type {ProyectoDTO} from '../../../api/Objetos';
import {useAuth} from '../../../app/providers/AuthProvider';
import {formatMoney} from '../../shared/utils/format';
import {AdminPageHeader} from '../ui/AdminPageHeader';
import {AdminStatCard} from '../ui/AdminStatCard';
import {AdminContentCard} from '../ui/AdminContentCard';
import {AdminToolbar} from '../ui/AdminToolbar';
import '../ui/adminUi.css';
import BarraCargando from "../../../ui/BarraCargando";
import { useTranslation } from 'react-i18next';
import { slugify } from '../../shared/utils/slugify';
import { useNotifications } from '../../../ui/notifications';

type ProyectoConSlug = ProyectoDTO & { slug: string };

const statusOptions = {
    'Borrador': 'informacionGeneralSection.statusOptions.Borrador',
    'Solicitado': 'informacionGeneralSection.statusOptions.Solicitado',
    'En Valoracion': 'informacionGeneralSection.statusOptions.En Valoracion',
    'Concedido': 'informacionGeneralSection.statusOptions.Concedido',
    'En ejecución': 'informacionGeneralSection.statusOptions.En Ejecucion',
    'Justificacion': 'informacionGeneralSection.statusOptions.Justificacion',
    'Denegada': 'informacionGeneralSection.statusOptions.Denegada',
    'Proceso auditoria': 'informacionGeneralSection.statusOptions.Proceso auditoria',
    'Ejecutada': 'informacionGeneralSection.statusOptions.Ejecutada',
};

export function AdminProjectsListPage() {
    const { t } = useTranslation();
    const auth = useAuth();
    const queryClient = useQueryClient();
    const { showSuccess, showError } = useNotifications();

    const [q, setQ] = useState('');
    const [estadoFilter, setEstadoFilter] = useState<string>('');
    const qDeferred = useDeferredValue(q);

    const proyectosQ = useQuery({
        queryKey: ['admin', 'proyectos', 'list', { q: qDeferred, estado: estadoFilter }],
        queryFn: () => proyectosApi.list({ q: qDeferred, estado: estadoFilter }),
        select: (data: unknown): ProyectoConSlug[] => {
            if (!Array.isArray(data)) {
                throw new Error('Respuesta de API inválida: no es un array.');
            }
            return data.map(proyecto => {
                if (typeof proyecto?.id !== 'number') {
                    throw new Error('Un elemento de la API no tiene el formato de proyecto esperado.');
                }
                const p = proyecto as ProyectoDTO;
                return { ...p, slug: slugify(p.informacionGeneral?.denominacion ?? '') };
            });
        },
    });

    const resumenQ = useQuery({
        queryKey: ['admin', 'proyectos', 'resumen'],
        queryFn: () => proyectosApi.getResumen(),
    });

    const deleteM = useMutation({
        mutationFn: (id: number) => proyectosApi.delete(id),
        onSuccess: async () => {
            showSuccess(t('adminProjectsList.deleteSuccess'));
            await queryClient.invalidateQueries({queryKey: ['admin', 'proyectos']});
            await queryClient.invalidateQueries({queryKey: ['public', 'proyectos']});
        },
        onError: (error) => {
            showError(t('adminProjectsList.errorDeleting'), renderErr(error));
        }
    });

    const items = proyectosQ.data ?? [];

    return (
        <div className="adminPageWrap">
            <AdminPageHeader
                eyebrow={t('adminProjectsList.management')}
                title={t('adminProjectsList.projects')}
                description={t('adminProjectsList.fullManagement')}
                actions={
                    <>
                        {auth.isAdmin ? (
                            <Link to="/administracion/proyectos/nuevo" className="btn btnPrimary">
                                {t('adminProjectsList.newProject')}
                            </Link>
                        ) : null}
                    </>
                }
            />
            <section className="adminStatsGrid">
                <AdminStatCard label={t('adminProjectsList.totalProjects')} value={resumenQ.isLoading ? <BarraCargando /> : (resumenQ.data?.totalProyectos ?? 0)} />
                <AdminStatCard label={t('adminProjectsList.withStatus')} value={resumenQ.isLoading ? <BarraCargando /> : (resumenQ.data?.proyectosConEstado ?? 0)}/>
                <AdminStatCard label={t('adminProjectsList.totalGrant')} value={resumenQ.isLoading ? <BarraCargando /> : formatMoney(resumenQ.data?.subvencionTotal ?? 0)}/>
                <AdminStatCard label={t('adminProjectsList.associatedContracts')} value={resumenQ.isLoading ? <BarraCargando /> : (resumenQ.data?.contratosAsociados ?? 0)}/>
            </section>

            <AdminContentCard
                title={t('adminProjectsList.projectsList')}
                description={t('adminProjectsList.filterAndCheck')}
            >
                <AdminToolbar
                    left={
                        <>
                            <div className="adminToolbarField">
                                <label htmlFor="admin-project-search">{t('adminProjectsList.search')}</label>
                                <input
                                    id="admin-project-search"
                                    className="input"
                                    placeholder={t('adminProjectsList.searchPlaceholder')}
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                />
                            </div>

                            <div className="adminToolbarField">
                                <label htmlFor="admin-project-estado">{t('adminProjectsList.status')}</label>
                                <select
                                    id="admin-project-estado"
                                    className="input select"
                                    value={estadoFilter}
                                    onChange={(e) => setEstadoFilter(e.target.value)}
                                >
                                    <option value="">{t('adminProjectsList.all')}</option>
                                    {Object.entries(statusOptions).map(([value, labelKey]) => (
                                        <option key={value} value={value}>
                                            {t(labelKey, value)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </>
                    }
                    right={<div
                        className="muted">{items.length} {items.length === 1 ? t('adminProjectsList.result') : t('adminProjectsList.results')}</div>}
                />

                {proyectosQ.isLoading ? <p>{t('adminProjectsList.loading')}</p> : null}

                {proyectosQ.isError ? (
                    <div className="alert alertError">
                        <strong>{t('adminProjectsList.errorListing')}</strong>
                        <div className="muted">{renderErr(proyectosQ.error)}</div>
                    </div>
                ) : null}

                <div className="adminTableWrap">
                    <table className="adminTable">
                        <thead>
                        <tr>
                            <th>{t('adminProjectsList.project')}</th>
                            <th>{t('adminProjectsList.status')}</th>
                            <th>{t('adminProjectsList.expMinistry')}</th>
                            <th>{t('adminProjectsList.grant')}</th>
                            <th>{t('adminProjectsList.contracts')}</th>
                            <th/>
                        </tr>
                        </thead>

                        <tbody>
                        {items.map((item: ProyectoConSlug) => {
                            const totalContratosProyecto =
                                (item.datosContratos?.contratoMayores?.length ?? 0) + (item.datosContratos?.contratoMenores?.length ?? 0);

                            return (
                                <tr key={item.id}>
                                    <td>
                                        <div style={{fontWeight: 700, color: '#111827'}}>
                                            {item.informacionGeneral?.denominacion || '-'}
                                        </div>
                                        <div className="muted">
                                            {item.informacionGeneral?.numeroExpedienteAyuntamiento || t('adminProjectsList.noTownHallId')}
                                        </div>
                                    </td>

                                    <td>
                                      <span className={`adminBadge ${getEstadoBadgeClass(item.informacionGeneral?.estado)}`}>
                                        {item.informacionGeneral?.estado || t('adminProjectsList.noStatus')}
                                      </span>
                                    </td>

                                    <td>{item.informacionGeneral?.numeroExpedienteMinisterio || '-'}</td>
                                    <td>{formatMoney(item.informacionGeneral?.importeSubvencionado)}</td>
                                    <td>{totalContratosProyecto}</td>

                                    <td>
                                        <div className="adminTableActions">
                                            <Link className="btn btnSmall btnSecondary"
                                                  to={`/proyectos/${item.slug}`}>
                                                {t('adminProjectsList.public')}
                                            </Link>

                                            <Link className="btn btnSmall btnPrimary"
                                                  to={`/administracion/proyectos/${item.slug}`}>
                                                {t(auth.isAdmin ? 'adminProjectsList.edit' : 'adminProjectsList.consult')}
                                            </Link>

                                            {auth.isAdmin ? (
                                                <button
                                                    className="btn btnSmall btnDanger"
                                                    type="button"
                                                    disabled={deleteM.isPending}
                                                    onClick={() => {
                                                        if (!window.confirm(t('adminProjectsList.deleteConfirm', { name: item.informacionGeneral?.denominacion || '' }))) return;
                                                        if (item.id) deleteM.mutate(item.id);
                                                    }}
                                                >
                                                    {t('adminProjectsList.delete')}
                                                </button>
                                            ) : null}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}

                        {!proyectosQ.isLoading && items.length === 0 ? (
                            <tr>
                                <td colSpan={6}>
                                    <div className="adminEmptyState">{t('adminProjectsList.noProjects')}
                                    </div>
                                </td>
                            </tr>
                        ) : null}
                        </tbody>
                    </table>
                </div>
            </AdminContentCard>
        </div>
    );
}

function renderErr(err: unknown) {
    if (err instanceof ApiError) return `${err.status}: ${err.message}`;
    if (err instanceof Error) return err.message;
    return String(err);
}

function getEstadoBadgeClass(value?: string) {
    const normalized = String(value ?? '').toLowerCase();

    if (!normalized) return 'isGray';
    if (
        normalized.includes('ejecut') ||
        normalized.includes('finaliz') ||
        normalized.includes('justific')
    ) {
        return 'isGreen';
    }
    if (
        normalized.includes('tramit') ||
        normalized.includes('licit') ||
        normalized.includes('curso')
    ) {
        return 'isBlue';
    }
    return 'isAmber';
}
